import { unpackToDateTime } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useCampfireQuery } from '../../network/useCampfireQuery';
import { GET_ORG_INFO } from './use-org-info.gql';
import { GetOrgInfo, GetOrgInfo_tm_tenant_billing_package as packageConfigInterface } from './__generated__/GetOrgInfo';

export type MenuLabels = {
  dashboardHome?: string | null;
  dashboardResources?: string | null;
  dashboardImpact?: string | null;
  volunteeringActivities?: string | null;
  volunteeringActivityReport?: string | null;
  volunteeringIncidentReport?: string | null;
  managementDashboard?: string | null;
  managementActivityManagement?: string | null;
  managementRosters?: string | null;
  managementVolunteers?: string | null;
  managementBulletins?: string | null;
  managementReportManagement?: string | null;
  managementIncidentManagement?: string | null;
  adminTaskManagement?: string | null;
  adminFormsSurveys?: string | null;
  adminProgramManagement?: string | null;
  adminConsole?: string | null;
  wordActivity?: string | null;
  wordRoster?: string | null;
  wordProgram?: string | null;
};

export type OrgInfo = {
  name: string;
  tenantId: string;
  dateCreated: DateTime;
  dateDeactivated?: DateTime;
  isDeactivated: boolean;
  smsEnabled: boolean;
  isLargeOrg: boolean;
  packageConfig?: packageConfigInterface;
  inTrial: boolean;
  menuLabels?: MenuLabels | null;
  availabilityRestrictionDays?: number | null;
};

export const useOrgInfo = (): undefined | OrgInfo => {
  const query = useCampfireQuery<GetOrgInfo, {}>(GET_ORG_INFO, {
    options: { fetchPolicy: 'cache-first' },
  });

  const gear = useMemo(() => {
    if (!query.data) return undefined;

    const deactiveDate = query.data.tm.tenant?.dateDeactivated;

    const dateDeactivated = deactiveDate ? unpackToDateTime(deactiveDate) : undefined;
    const isDeactivated = dateDeactivated === undefined ? false : dateDeactivated.diffNow('hours').hours < 0;

    return {
      name: query.data.orgName,
      tenantId: query.data.orgId,
      dateCreated: unpackToDateTime(query.data.orgDateCreated),
      dateDeactivated,
      isDeactivated,
      smsEnabled: query.data.smsEnabled,
      isLargeOrg: query.data.isLargeOrg,
      packageConfig: query.data.tm.tenant.billing?.package ?? undefined,
      inTrial: !!query.data.tm.tenant.config?.inTrial,
      menuLabels: query.data.tm.tenant.menuLabels ?? undefined,
      availabilityRestrictionDays: query.data.tm.tenant.config?.availabilityRestrictionDays ?? null
    };
  }, [query.data]);

  return gear;
};
